/**
 * useJointJSCanvas Hook
 * Initializes and manages JointJS canvas (graph and paper)
 */

import { useEffect, useRef, useState, RefObject } from 'react'
import { dia, shapes, g } from '@joint/core'
import { getFlag } from '../../../config/featureFlags'
import { perfMonitor } from '../../../map-editor-shared/perf/perfMonitor'

interface UseJointJSCanvasReturn {
  graph: dia.Graph | null
  paper: dia.Paper | null
}

export function useJointJSCanvas(
  canvasRef: RefObject<HTMLDivElement>,
  canvasId?: string // Add optional canvasId for unique identification
): UseJointJSCanvasReturn {
  const [graph, setGraph] = useState<dia.Graph | null>(null)
  const [paper, setPaper] = useState<dia.Paper | null>(null)
  const graphRef = useRef<dia.Graph | null>(null)
  const paperRef = useRef<dia.Paper | null>(null)
  const initializedRef = useRef<boolean>(false)
  const viewportRafRef = useRef<number | null>(null)
  const visibleAreaRef = useRef<g.Rect | null>(null)

  useEffect(() => {
    // Prevent double initialization
    if (initializedRef.current) {
      return
    }

    // Wait for canvasRef.current to be available
    if (!canvasRef.current) {
      // Poll until canvas is available
      const checkInterval = setInterval(() => {
        if (canvasRef.current && !initializedRef.current) {
          clearInterval(checkInterval)
          initializeCanvas()
        }
      }, 50)

      return () => clearInterval(checkInterval)
    }

    // Canvas is already available, initialize immediately
    initializeCanvas()

    function initializeCanvas() {
      if (initializedRef.current || !canvasRef.current) return

      const logPrefix = canvasId || 'Canvas'

      // Mark as initialized
      initializedRef.current = true

      // Create graph with optimized settings
      const newGraph = new dia.Graph({}, {
        cellNamespace: shapes,
        // Performance optimization: disable automatic sorting during batch operations
        // We'll manually sort elements by z-index when needed
        // This prevents expensive sorting operations on every cell addition
      })

      // Enable batch mode for initial setup
      // This defers all graph events until stopBatch() is called
      // Significantly improves performance when adding many elements at once
      newGraph.startBatch('init')

      graphRef.current = newGraph
      setGraph(newGraph)

      // Stop batch mode after a short delay to allow initial setup
      setTimeout(() => {
        if (graphRef.current) {
          graphRef.current.stopBatch('init')
        }
      }, 100)

      // 배경은 토큰 한 곳에서만 온다 — 여기서 data-theme 를 따로 읽어 색을 박으면
      // 마운트 때와 useThemeSync(토글) 때 배경이 달라진다.
      const backgroundColor =
        getComputedStyle(document.documentElement).getPropertyValue('--color-canvas-bg').trim() ||
        '#ffffff'

      // Create paper with optimized settings for large datasets
      const newPaper = new dia.Paper({
        model: newGraph,
        width: 20000,
        height: 20000,
        gridSize: 1, // Keep small gridSize for potential snapping calculations
        drawGrid: false, // Grid disabled - using Smart Guides instead
        background: { color: backgroundColor },
        cellViewNamespace: shapes,
        // 객체 위치는 CSV/도면 좌표가 기준이라 마우스 드래그 이동을 막는다(선택·클릭은 유지).
        interactive: { elementMove: false },
        async: true,
        sorting: dia.Paper.sorting.APPROX,
        // Performance optimizations for large datasets (3000+ elements)
        // Phase 1b: when CANVAS_EDITOR is on, keep the paper frozen so it never
        // builds SVG cell views (the Canvas overlay renders instead). Flag off =
        // false (unchanged). The restore path (graph.fromJSON) has no unfreeze,
        // so frozen:true keeps the SVG empty end-to-end.
        frozen: getFlag('CANVAS_EDITOR'),

        preventDefaultBlankAction: false, // Prevent default drag behavior
        preventContextMenu: false,
        clickThreshold: 0, // Minimize click detection overhead
        moveThreshold: 0, // Minimize move detection overhead
        magnetThreshold: 0, // Disable magnet snapping
        // Viewport culling: only mount SVG views for cells intersecting the
        // visible area (+30% padding). Bounds the SVG DOM node count to the
        // viewport instead of the whole map — the dominant perf cost on large
        // DXF imports (tens of thousands of entities). All cells remain in the
        // graph model (save / relationships / zone-dong mapping unaffected);
        // only off-screen *rendering* is deferred. Links are never culled so
        // relationship/overlay lines don't flicker. checkViewport() is wired to
        // pan/zoom below so newly-revealed cells mount on transform.
        //
        // NOTE: paper.getArea() is NOT used — it derives from the paper's fixed
        // 20000×20000 size and would never cull. The real visible region is the
        // clipping container (paper.el.parentElement), computed once per frame
        // and cached (visibleAreaRef) so getBoundingClientRect runs once per
        // checkViewport pass, not once per cell.
        viewport: (view: dia.CellView): boolean => {
          const model = view.model
          if (model.isLink && model.isLink()) return true
          const area = visibleAreaRef.current
          if (!area) return true
          return Boolean(area.intersect(model.getBBox()))
        },
      })
      paperRef.current = newPaper
      setPaper(newPaper)

      // Compute the visible region (in paper-local coords) from the clipping
      // container, inflated 30% so cells just outside the edge are pre-mounted.
      const computeVisibleArea = (): g.Rect | null => {
        const containerEl = newPaper.el.parentElement
        if (!containerEl) return null
        const rect = containerEl.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) return null
        const tl = newPaper.clientToLocalPoint({ x: rect.left, y: rect.top })
        const br = newPaper.clientToLocalPoint({ x: rect.right, y: rect.bottom })
        const local = new g.Rect(
          Math.min(tl.x, br.x),
          Math.min(tl.y, br.y),
          Math.abs(br.x - tl.x),
          Math.abs(br.y - tl.y)
        )
        return local.inflate(local.width * 0.3, local.height * 0.3)
      }
      visibleAreaRef.current = computeVisibleArea()

      // Re-evaluate the viewport (mount/unmount cell views) after pan/zoom.
      // Both panning (paper.translate) and zoom (paper.scale + translate) and
      // scaleContentToFit emit 'translate'/'scale', so this single listener
      // covers every transform path. Coalesced to one check per animation frame
      // to avoid thrashing during trackpad inertia / drag.
      const scheduleViewportCheck = () => {
        if (viewportRafRef.current !== null) return
        viewportRafRef.current = requestAnimationFrame(() => {
          viewportRafRef.current = null
          if (!initializedRef.current) return
          visibleAreaRef.current = computeVisibleArea()
          try {
            newPaper.checkViewport()
          } catch {
            // Paper may be mid-teardown (HMR / unmount) — safe to ignore.
          }
        })
      }
      newPaper.on('translate', scheduleViewportCheck)
      newPaper.on('scale', scheduleViewportCheck)

      // Clear any existing content in container
      if (canvasRef.current!.firstChild) {
        canvasRef.current!.innerHTML = ''
      }

      // Append paper to container
      canvasRef.current!.appendChild(newPaper.el)

      // --- Phase 0 (Canvas migration): render-path flag + perf instrumentation ---
      // CANVAS_EDITOR is the branch point for Phase 1, where the unified Canvas
      // renderer will replace the SVG Paper. No behavior change yet.
      if (getFlag('CANVAS_EDITOR')) {
        console.info(
          `[${logPrefix}] CANVAS_EDITOR flag on — Canvas-only render path not yet implemented (Phase 1). Using JointJS Paper.`
        )
      }
      // PERF_HUD measures the canvas container's document-attached DOM (the SVG
      // subtree in flag-off, the Canvas element in flag-on) — the count we aim
      // to eliminate.
      if (getFlag('PERF_HUD')) {
        perfMonitor.start(canvasRef.current)
        ;(window as any).__editorGraph = newGraph // dev diagnostic (PERF_HUD only)
      }
    }

    // Cleanup
    return () => {
      perfMonitor.stop()
      if (viewportRafRef.current !== null) {
        cancelAnimationFrame(viewportRafRef.current)
        viewportRafRef.current = null
      }
      if (graphRef.current && paperRef.current) {
        paperRef.current.remove()
        graphRef.current.clear()
      }
      initializedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty array - run only on mount, polling handles canvas availability

  return { graph, paper }
}
